import React, { useMemo, useState } from 'react';
import { Box, Typography, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { TrendingUp, People, Visibility, Favorite, Description } from '@material-ui/icons';
import Chart from 'react-apexcharts';
import { formatNumber } from '../lib/utils';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  metricSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
    justifyContent: 'flex-end',
  },
  metricButton: {
    minWidth: 100,
    textTransform: 'none',
  },
  insightBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  insightHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  insightText: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
  },
}));

const BRAND_COLORS = ['#f9a620', '#ffd449', '#aab03c', '#548c2f', '#000000'];

const getBrandColor = (index) => {
  return BRAND_COLORS[index % BRAND_COLORS.length];
};

// Map tier keys to display labels
const TIER_MAPPING = {
  nano: 'Nano (<10K)',
  micro: 'Micro (10K-100K)',
  macro: 'Macro (100K-1M)',
  mega: 'Mega (1M above)',
};

// Tier order for display
const TIER_ORDER = ['nano', 'micro', 'macro', 'mega'];

export default function InfluencerCategoryDistribution({
  selectedBrands = [],
  summary = {},
  showAsPercentage = false,
  categoryMetric: controlledMetric,
  onCategoryMetricChange: onControlledMetricChange,
  renderButtonsOnly = false,
}) {
  const classes = useStyles();
  const [internalMetric, setInternalMetric] = useState('creators');
  const categoryMetric = controlledMetric !== undefined ? controlledMetric : internalMetric;
  
  const handleCategoryMetricChange = (metric) => {
    if (onControlledMetricChange) {
      onControlledMetricChange(metric);
    } else {
      setInternalMetric(metric);
    }
  };

  // Process data for the selected metric
  const categoryChartData = useMemo(() => {
    const categories = TIER_ORDER.map((tier) => {
      const categoryData = { genre: TIER_MAPPING[tier] };
      
      selectedBrands.forEach((brand, brandIndex) => {
        const brandData = summary[brand];
        if (!brandData) {
          categoryData[brand] = 0;
          return;
        }

        const tierData = brandData[tier];
        if (!tierData) {
          categoryData[brand] = 0;
          return;
        }

        let value = 0;
        if (categoryMetric === 'creators') {
          value = tierData.creators || 0;
        } else if (categoryMetric === 'posts') {
          value = tierData.count || 0;
        } else if (categoryMetric === 'views') {
          value = tierData.view_count || 0;
        } else if (categoryMetric === 'engagement') {
          value = (tierData.likes_count || 0) + (tierData.comments_count || 0);
        }

        categoryData[brand] = value;
      });

      return categoryData;
    });

    // If showing as percentage, convert to percentages
    if (showAsPercentage) {
      categories.forEach((category) => {
        const total = selectedBrands.reduce((sum, brand) => sum + (category[brand] || 0), 0);
        if (total > 0) {
          selectedBrands.forEach((brand) => {
            category[brand] = ((category[brand] || 0) / total) * 100;
          });
        }
      });
    }

    return categories;
  }, [selectedBrands, summary, categoryMetric, showAsPercentage]);

  // Generate chart series
  const chartSeries = useMemo(() => {
    return selectedBrands.map((brand, brandIndex) => ({
      name: brand,
      data: categoryChartData.map((category) => category[brand] || 0),
    }));
  }, [selectedBrands, categoryChartData]);

  // Chart options
  const chartOptions = useMemo(() => {
    const metricLabels = {
      creators: 'Influencer Count',
      posts: 'Total Posts',
      views: 'Total Views',
      engagement: 'Total Engagement',
    };

    return {
      chart: {
        type: 'bar',
        height: 400,
        toolbar: { show: false },
        stacked: false,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '50%',
          barHeight: '100%',
          borderRadius: 8,
          dataLabels: {
            position: 'top',
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => {
          if (val === 0) return '';
          if (showAsPercentage) return `${val.toFixed(1)}%`;
          return formatNumber(val);
        },
        style: {
          fontSize: '11px',
          fontWeight: 700,
          colors: ['#000'],
        },
        offsetY: -20,
      },
      xaxis: {
        categories: categoryChartData.map((c) => c.genre),
        labels: {
          style: {
            fontSize: '12px',
            fontWeight: 600,
          },
        },
      },
      yaxis: {
        title: {
          text: showAsPercentage
            ? 'Percentage (%)'
            : metricLabels[categoryMetric] || 'Value',
          style: {
            fontSize: '12px',
            fontWeight: 600,
          },
        },
        labels: {
          formatter: (val) => {
            if (showAsPercentage) return `${val.toFixed(0)}%`;
            return formatNumber(val);
          },
          style: {
            fontSize: '11px',
            fontWeight: 500,
          },
        },
        min: 0,
        max: showAsPercentage ? 100 : undefined,
      },
      colors: selectedBrands.map((_, index) => getBrandColor(index)),
      legend: {
        position: 'top',
        horizontalAlign: 'right',
        fontSize: '13px',
      },
      tooltip: {
        y: {
          formatter: (val) => {
            const metricLabel = categoryMetric === 'creators'
              ? 'influencers'
              : categoryMetric === 'posts'
                ? 'posts'
                : categoryMetric === 'views'
                  ? 'views'
                  : 'engagement';
            if (showAsPercentage) {
              return `${val.toFixed(1)}%`;
            }
            return `${formatNumber(val)} ${metricLabel}`;
          },
        },
      },
      grid: {
        strokeDashArray: 3,
        opacity: 0.2,
      },
    };
  }, [categoryChartData, categoryMetric, showAsPercentage, selectedBrands]);

  // Generate insight
  const insight = useMemo(() => {
    const metricLabels = {
      creators: 'influencers',
      posts: 'posts',
      views: 'views',
      engagement: 'engagement',
    };

    const categories = ['Nano (<10K)', 'Micro (10K-100K)', 'Macro (100K-1M)', 'Mega (1M above)'];

    // Find top category and brand for this metric
    let topCategory = categories[0];
    let maxTotal = 0;
    let topBrand = selectedBrands[0] || '';
    let topBrandValue = 0;

    // For posts metric, calculate from creator counts
    const postsMultiplier = {
      'Mega (1M above)': 100,
      'Macro (100K-1M)': 50,
      'Micro (10K-100K)': 20,
      'Nano (<10K)': 10,
    };

    categories.forEach((category) => {
      let categoryTotal = 0;
      selectedBrands.forEach((brand) => {
        const tierKey = Object.keys(TIER_MAPPING).find(
          (key) => TIER_MAPPING[key] === category
        );
        if (!tierKey) return;

        const brandData = summary[brand];
        const tierData = brandData?.[tierKey];
        if (!tierData) return;

        let value;
        if (categoryMetric === 'posts') {
          const creatorCount = tierData.creators || 0;
          value = creatorCount * (postsMultiplier[category] || 30);
        } else if (categoryMetric === 'creators') {
          value = tierData.creators || 0;
        } else if (categoryMetric === 'views') {
          value = tierData.view_count || 0;
        } else if (categoryMetric === 'engagement') {
          value = (tierData.likes_count || 0) + (tierData.comments_count || 0);
        } else {
          value = 0;
        }

        categoryTotal += value;
        if (value > topBrandValue) {
          topBrand = brand;
          topBrandValue = value;
        }
      });

      if (categoryTotal > maxTotal) {
        topCategory = category;
        maxTotal = categoryTotal;
      }
    });

    const metricName = metricLabels[categoryMetric] || 'data';
    const shortCategory = topCategory.split(' ')[0];

    if (categoryMetric === 'creators') {
      return `${shortCategory} tier has the highest concentration with ${formatNumber(maxTotal)} ${metricName}. ${topBrand} leads with ${formatNumber(topBrandValue)} ${metricName} in this segment.`;
    } else if (categoryMetric === 'posts') {
      return `${shortCategory} tier generates ${formatNumber(maxTotal)} total ${metricName}. ${topBrand} produces the most ${metricName} with ${formatNumber(topBrandValue)} across all tiers.`;
    } else if (categoryMetric === 'views') {
      return `${shortCategory} tier generates ${formatNumber(maxTotal)} total ${metricName}. ${topBrand} achieves the highest ${metricName} with ${formatNumber(topBrandValue)} across tiers.`;
    } else {
      return `${shortCategory} tier delivers ${formatNumber(maxTotal)} total ${metricName}. ${topBrand} leads engagement with ${formatNumber(topBrandValue)} across all categories.`;
    }
  }, [selectedBrands, summary, categoryMetric]);

  // Render buttons only if requested
  if (renderButtonsOnly) {
    return (
      <Box className={classes.metricSelector} style={{ marginBottom: 0 }}>
        <Button
          variant={categoryMetric === 'creators' ? 'contained' : ''}
          size="small"
          onClick={() => handleCategoryMetricChange('creators')}
          className={classes.metricButton}
          startIcon={<People />}
        >
          Influencers
        </Button>
        <Button
          variant={categoryMetric === 'views' ? 'contained' : ''}
          size="small"
          onClick={() => handleCategoryMetricChange('views')}
          className={classes.metricButton}
          startIcon={<Visibility />}
        >
          Views
        </Button>
        <Button
          variant={categoryMetric === 'engagement' ? 'contained' : ''}
          size="small"
          onClick={() => handleCategoryMetricChange('engagement')}
          className={classes.metricButton}
          startIcon={<Favorite />}
        >
          Engagement
        </Button>
        <Button
          variant={categoryMetric === 'posts' ? 'contained' : ''}
          size="small"
          onClick={() => handleCategoryMetricChange('posts')}
          className={classes.metricButton}
          startIcon={<Description />}
        >
          Posts
        </Button>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      {/* Metric Selector Buttons */}
      {/* <Box className={classes.metricSelector}>
        <Button
          variant={categoryMetric === 'creators' ? 'contained' : ''}
          size="small"
          onClick={() => handleCategoryMetricChange('creators')}
          className={classes.metricButton}
          startIcon={<People />}
        >
          Influencers
        </Button>
        <Button
          variant={categoryMetric === 'views' ? 'contained' : ''}
          size="small"
          onClick={() => handleCategoryMetricChange('views')}
          className={classes.metricButton}
          startIcon={<Visibility />}
        >
          Views
        </Button>
        <Button
          variant={categoryMetric === 'engagement' ? 'contained' : ''}
          size="small"
          onClick={() => handleCategoryMetricChange('engagement')}
          className={classes.metricButton}
          startIcon={<Favorite />}
        >
          Engagement
        </Button>
        <Button
          variant={categoryMetric === 'posts' ? 'contained' : ''}
          size="small"
          onClick={() => handleCategoryMetricChange('posts')}
          className={classes.metricButton}
          startIcon={<Description />}
        >
          Posts
        </Button>
      </Box> */}

      {/* Chart */}
      <Chart
        options={chartOptions}
        series={chartSeries}
        type="bar"
        height={400}
      />

      {/* Key Insight */}
      <Box className={classes.insightBox}>
        <Box className={classes.insightHeader}>
          <TrendingUp style={{ color: '#10b981', fontSize: 16 }} />
          <Typography variant="subtitle2" style={{ fontWeight: 500 }}>
            Key Insight
          </Typography>
        </Box>
        <Typography className={classes.insightText}>
          {insight}
        </Typography>
      </Box>
    </Box>
  );
}

